/**
 * UsersPage.jsx — full user management for admins.
 *
 * Per user: Approve, Block, Set pending, Edit profile,
 *           Promote/demote role, Reset password, Delete.
 */

import { useEffect, useState } from "react";
import { adminGetUsers, adminSetStatus, adminDeleteUser,
         adminUpdateRole, adminUpdateProfile, adminResetPassword } from "../../services/api";

const STATUS_BADGE = {
  pending: "badge-pending",
  active:  "badge-active",
  blocked: "badge-blocked",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });
}

export default function UsersPage() {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [busy,    setBusy]    = useState(null);
  const [panel,   setPanel]   = useState(null);   // { type: "edit"|"reset", userId }
  const [panelMsg, setPanelMsg] = useState({ text: "", ok: true });

  // Edit form state
  const [editName,  setEditName]  = useState("");
  const [editEmail, setEditEmail] = useState("");

  // Reset password state
  const [resetPwd, setResetPwd] = useState("");

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true); setError("");
    try   { setUsers(await adminGetUsers()); }
    catch (e) { setError(e.message); }
    finally   { setLoading(false); }
  }

  function openPanel(type, user) {
    setPanel({ type, userId: user.id });
    setPanelMsg({ text: "", ok: true });
    setResetPwd("");
    if (type === "edit") { setEditName(user.name); setEditEmail(user.email); }
  }

  function closePanel() { setPanel(null); setPanelMsg({ text: "", ok: true }); }

  async function act(fn, userId) {
    setBusy(userId);
    try { await fn(); await load(); }
    catch (e) { setError(e.message); }
    finally   { setBusy(null); }
  }

  async function submitEdit(e, userId) {
    e.preventDefault();
    setBusy(userId); setPanelMsg({ text: "", ok: true });
    try {
      await adminUpdateProfile(userId, editName, editEmail);
      setPanelMsg({ text: "Profile updated.", ok: true });
      await load();
      setTimeout(closePanel, 1500);
    } catch (err) { setPanelMsg({ text: err.message, ok: false }); }
    finally { setBusy(null); }
  }

  async function submitReset(e, userId) {
    e.preventDefault();
    if (resetPwd.length < 8) { setPanelMsg({ text: "Minimum 8 characters.", ok: false }); return; }
    setBusy(userId); setPanelMsg({ text: "", ok: true });
    try {
      const data = await adminResetPassword(userId, resetPwd);
      setPanelMsg({ text: data.message, ok: true });
      setResetPwd("");
      setTimeout(closePanel, 2000);
    } catch (err) { setPanelMsg({ text: err.message, ok: false }); }
    finally { setBusy(null); }
  }

  return (
    <div className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Users</h2>
        <button className="btn btn-outline-secondary btn-sm" onClick={load} disabled={loading}>Refresh</button>
      </div>

      {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
      {loading && <div className="text-muted" style={{ fontSize: 14 }}>Loading…</div>}

      {!loading && (
        <div style={{ background: "#fff", border: "1px solid #dee2e6", borderRadius: 12, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table className="table table-hover mb-0" style={{ fontSize: 13, minWidth: 700 }}>
              <thead style={{ background: "#f8f9fa" }}>
                <tr>
                  <th className="ps-3 py-2 fw-semibold text-muted">Name</th>
                  <th className="py-2 fw-semibold text-muted">Email</th>
                  <th className="py-2 fw-semibold text-muted">Role</th>
                  <th className="py-2 fw-semibold text-muted">Status</th>
                  <th className="py-2 fw-semibold text-muted">Registered</th>
                  <th className="pe-3 py-2" style={{ minWidth: 300 }}></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <>
                    <tr key={u.id}>
                      <td className="ps-3 py-3 align-middle fw-medium">{u.name}</td>
                      <td className="py-3 align-middle text-muted" style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</td>
                      <td className="py-3 align-middle">
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                          background: u.role === "admin" ? "#E6F1FB" : u.role === "risk_manager" ? "#EDF5E0" : "#f1f3f5",
                          color:      u.role === "admin" ? "#185FA5" : u.role === "risk_manager" ? "#2d6e1f"  : "#495057" }}>
                          {u.role === "risk_manager" ? "risk manager" : u.role}
                        </span>
                      </td>
                      <td className="py-3 align-middle">
                        <span className={`badge ${STATUS_BADGE[u.status] ?? ""}`}
                              style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 4 }}>
                          {u.status}
                        </span>
                        {u.must_change_password && (
                          <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 600, padding: "2px 6px",
                            borderRadius: 4, background: "#FAEEDA", color: "#633806" }}>
                            pwd reset
                          </span>
                        )}
                      </td>
                      <td className="py-3 align-middle text-muted">{fmtDate(u.created_at)}</td>
                      <td className="pe-3 py-3 align-middle">
                        {u.role !== "admin" || true ? (
                          <div className="d-flex gap-1 flex-wrap justify-content-end">
                            {u.role !== "admin" && u.status !== "active"  && (
                              <button className="btn btn-sm btn-success" style={{ fontSize: 11 }}
                                      onClick={() => act(() => adminSetStatus(u.id,"active"), u.id)} disabled={busy===u.id}>Approve</button>
                            )}
                            {u.role !== "admin" && u.status !== "blocked" && (
                              <button className="btn btn-sm btn-warning" style={{ fontSize: 11 }}
                                      onClick={() => act(() => adminSetStatus(u.id,"blocked"), u.id)} disabled={busy===u.id}>Block</button>
                            )}
                            <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }}
                                    onClick={() => openPanel(panel?.userId===u.id && panel?.type==="edit" ? null : "edit", u)}>
                              Edit
                            </button>
                            {(u.role === "user" || u.role === "admin") && (
                              <button className="btn btn-sm btn-outline-success" style={{ fontSize: 11, color:"#2d6e1f", borderColor:"#4B7518" }}
                                      onClick={() => act(() => adminUpdateRole(u.id, "risk_manager"), u.id)} disabled={busy===u.id}>
                                Risk manager
                              </button>
                            )}
                            {u.role !== "admin" && (
                              <button className="btn btn-sm btn-outline-info" style={{ fontSize: 11, color:"#185FA5", borderColor:"#185FA5" }}
                                      onClick={() => act(() => adminUpdateRole(u.id, "admin"), u.id)} disabled={busy===u.id}>
                                Make admin
                              </button>
                            )}
                            {(u.role === "admin" || u.role === "risk_manager") && u.id !== undefined && (
                              <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: 11 }}
                                      onClick={() => act(() => adminUpdateRole(u.id, "user"), u.id)} disabled={busy===u.id}>
                                Demote to user
                              </button>
                            )}
                            <button className="btn btn-sm btn-outline-warning" style={{ fontSize: 11, color:"#854F0B", borderColor:"#854F0B" }}
                                    onClick={() => openPanel(panel?.userId===u.id && panel?.type==="reset" ? null : "reset", u)}>
                              Reset pwd
                            </button>
                            {u.role !== "admin" && (
                              <button className="btn btn-sm btn-outline-danger" style={{ fontSize: 11 }}
                                      onClick={() => { if(window.confirm(`Delete "${u.email}"?`)) act(() => adminDeleteUser(u.id), u.id); }} disabled={busy===u.id}>
                                Delete
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#adb5bd" }}>—</span>
                        )}
                      </td>
                    </tr>

                    {/* Inline Edit panel */}
                    {panel?.type === "edit" && panel?.userId === u.id && (
                      <tr key={`${u.id}-edit`} style={{ background: "#f8f9fa" }}>
                        <td colSpan={6} className="px-3 py-3">
                          {panelMsg.text && (
                            <div className={`alert py-1 px-2 mb-2 ${panelMsg.ok ? "alert-success" : "alert-danger"}`} style={{ fontSize: 12 }}>
                              {panelMsg.text}
                            </div>
                          )}
                          <form onSubmit={(e) => submitEdit(e, u.id)} className="d-flex gap-2 align-items-end flex-wrap">
                            <div>
                              <label style={{ fontSize: 12, fontWeight: 500 }}>Name</label>
                              <input className="form-control form-control-sm mt-1" style={{ width: 160 }}
                                     value={editName} onChange={(e) => setEditName(e.target.value)} required />
                            </div>
                            <div>
                              <label style={{ fontSize: 12, fontWeight: 500 }}>Email</label>
                              <input type="email" className="form-control form-control-sm mt-1" style={{ width: 210 }}
                                     value={editEmail} onChange={(e) => setEditEmail(e.target.value)} required />
                            </div>
                            <button className="btn btn-sm btn-primary" type="submit" disabled={busy === u.id}>Save</button>
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={closePanel}>Cancel</button>
                          </form>
                        </td>
                      </tr>
                    )}

                    {/* Inline Reset Password panel */}
                    {panel?.type === "reset" && panel?.userId === u.id && (
                      <tr key={`${u.id}-reset`} style={{ background: "#FAEEDA" }}>
                        <td colSpan={6} className="px-3 py-3">
                          {panelMsg.text && (
                            <div className={`alert py-1 px-2 mb-2 ${panelMsg.ok ? "alert-success" : "alert-danger"}`} style={{ fontSize: 12 }}>
                              {panelMsg.text}
                            </div>
                          )}
                          <p style={{ fontSize: 12, color: "#633806", margin: "0 0 8px", fontWeight: 500 }}>
                            Set temporary password for {u.name} - they will receive it by email and must change it on next login.
                          </p>
                          <form onSubmit={(e) => submitReset(e, u.id)} className="d-flex gap-2 align-items-center flex-wrap">
                            <input type="password" className="form-control form-control-sm" style={{ maxWidth: 220 }}
                                   value={resetPwd} onChange={(e) => setResetPwd(e.target.value)}
                                   placeholder="Temp password (min 8 chars)" required minLength={8} />
                            <button className="btn btn-sm btn-warning" type="submit" disabled={busy === u.id}>
                              {busy === u.id ? "Saving…" : "Send & save"}
                            </button>
                            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={closePanel}>Cancel</button>
                          </form>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
